/**
 * Runtime options for a single overlay instance.
 *
 * Everything is driven by query parameters because an OBS "Browser Source" is
 * configured by URL and nothing else - there is no settings dialog on a VPS at
 * three in the morning.
 *
 *   ?obs=1            hide the click-to-start gate, hide the cursor
 *   ?quality=low      drop the expensive filters on weak encoders
 *   ?mute=1           silence the music bed (stream has its own audio)
 *   ?speech=0         disable text-to-speech commentary
 *   ?hud=0            canvas only, for a clean second source
 *   ?server=https://… point at a remote broadcast server
 */

/**
 * The overlay is authored at a fixed pixel size and scaled to fit, so every
 * coordinate in the codebase is a real broadcast pixel. There are two such
 * sizes, because vertical and horizontal streams are different products:
 *
 *   portrait  1080x1920  TikTok Live, YouTube Shorts, Facebook Reels
 *   landscape 1920x1080  YouTube, Twitch, Facebook
 *
 * `DESIGN.width` / `DESIGN.height` are accessors rather than constants: the
 * orientation is only known once the URL has been read, and every module reads
 * them at use time rather than at import time.
 */
export type Orientation = 'portrait' | 'landscape';

export const PORTRAIT_SIZE = { width: 1080, height: 1920 } as const;
export const LANDSCAPE_SIZE = { width: 1920, height: 1080 } as const;

let currentOrientation: Orientation = 'portrait';

export const DESIGN = {
  get orientation(): Orientation {
    return currentOrientation;
  },
  get portrait(): boolean {
    return currentOrientation === 'portrait';
  },
  get width(): number {
    return currentOrientation === 'portrait' ? PORTRAIT_SIZE.width : LANDSCAPE_SIZE.width;
  },
  get height(): number {
    return currentOrientation === 'portrait' ? PORTRAIT_SIZE.height : LANDSCAPE_SIZE.height;
  },
  /** Centre of the frame - the default camera target. */
  get centreX(): number {
    return DESIGN.width / 2;
  },
  get centreY(): number {
    return DESIGN.height / 2;
  },
};

/**
 * Must run before anything builds geometry. `main.ts` calls it as its first act.
 */
export function setOrientation(orientation: Orientation): void {
  currentOrientation = orientation;
}

export type QualityTier = 'low' | 'medium' | 'high' | 'ultra';

export interface QualityProfile {
  readonly tier: QualityTier;
  readonly bloom: boolean;
  readonly godRays: boolean;
  readonly motionBlur: boolean;
  readonly glow: boolean;
  readonly chromaticAberration: boolean;
  /** Multiplier applied to every particle burst. */
  readonly particleScale: number;
  /** Hard ceiling on live particles. */
  readonly particleBudget: number;
  /** Renderer resolution multiplier. */
  readonly resolution: number;
  readonly antialias: boolean;
  /** Number of stars in the galaxy field. */
  readonly starCount: number;
}

const PROFILES: Record<QualityTier, QualityProfile> = {
  low: {
    tier: 'low',
    bloom: false,
    godRays: false,
    motionBlur: false,
    glow: false,
    chromaticAberration: false,
    particleScale: 0.35,
    particleBudget: 400,
    resolution: 1,
    antialias: false,
    starCount: 260,
  },
  medium: {
    tier: 'medium',
    bloom: true,
    godRays: false,
    motionBlur: false,
    glow: true,
    chromaticAberration: false,
    particleScale: 0.7,
    particleBudget: 1100,
    resolution: 1,
    antialias: true,
    starCount: 520,
  },
  high: {
    tier: 'high',
    bloom: true,
    godRays: true,
    motionBlur: true,
    glow: true,
    chromaticAberration: true,
    particleScale: 1,
    particleBudget: 2200,
    resolution: 1,
    antialias: true,
    starCount: 900,
  },
  ultra: {
    tier: 'ultra',
    bloom: true,
    godRays: true,
    motionBlur: true,
    glow: true,
    chromaticAberration: true,
    particleScale: 1.35,
    particleBudget: 3600,
    resolution: Math.min(2, typeof devicePixelRatio === 'number' ? devicePixelRatio : 1),
    antialias: true,
    starCount: 1400,
  },
};

export interface RuntimeOptions {
  readonly orientation: Orientation;
  readonly obsMode: boolean;
  readonly quality: QualityProfile;
  readonly muted: boolean;
  readonly speech: boolean;
  readonly hud: boolean;
  readonly serverUrl: string | undefined;
  readonly musicVolume: number;
  readonly sfxVolume: number;
  readonly voiceVolume: number;
}

function flag(params: URLSearchParams, key: string, fallback: boolean): boolean {
  const raw = params.get(key);
  if (raw === null) return fallback;
  return raw === '1' || raw === 'true' || raw === 'yes';
}

function volume(params: URLSearchParams, key: string, fallback: number): number {
  const raw = Number(params.get(key));
  return Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : fallback;
}

export function readRuntimeOptions(search = window.location.search): RuntimeOptions {
  const params = new URLSearchParams(search);

  const requested = (params.get('quality') ?? '').toLowerCase();
  const tier: QualityTier =
    requested === 'low' || requested === 'medium' || requested === 'high' || requested === 'ultra'
      ? requested
      : detectTier();

  // Explicit wins; otherwise infer from the window, so simply resizing an OBS
  // browser source to 1080x1920 does the right thing without a second setting.
  const requestedOrientation = (params.get('orientation') ?? '').toLowerCase();
  const orientation: Orientation =
    requestedOrientation === 'portrait' || requestedOrientation === 'landscape'
      ? requestedOrientation
      : typeof window !== 'undefined' && window.innerHeight > window.innerWidth
        ? 'portrait'
        : 'landscape';

  return {
    orientation,
    obsMode: flag(params, 'obs', false),
    quality: PROFILES[tier],
    muted: flag(params, 'mute', false),
    speech: flag(params, 'speech', true),
    hud: flag(params, 'hud', true),
    serverUrl: params.get('server') ?? undefined,
    musicVolume: volume(params, 'music', 0.5),
    sfxVolume: volume(params, 'sfx', 0.75),
    voiceVolume: volume(params, 'voice', 1),
  };
}

/**
 * Pick a sensible default tier.
 *
 * Deliberately conservative: a dropped frame on a live stream is far more
 * expensive than a slightly dimmer bloom, so anything that is not clearly a
 * capable desktop GPU starts at medium.
 */
function detectTier(): QualityTier {
  if (typeof navigator === 'undefined') return 'high';

  const cores = navigator.hardwareConcurrency ?? 4;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

  if (cores >= 12 && memory >= 8) return 'ultra';
  if (cores >= 6 && memory >= 6) return 'high';
  if (cores >= 4) return 'medium';
  return 'low';
}

export function resolveServerUrl(options: RuntimeOptions): string {
  if (options.serverUrl) return options.serverUrl;
  // Same origin in production; Vite proxies /live during development.
  return window.location.origin;
}
